import type {
  AiOperationsProposalRound,
  AiOperationsRun,
  AiOperationsRunAction,
} from "@adpilot/shared";
import {
  IconArrowLeft,
  IconCalendarTime,
  IconDatabaseOff,
  IconRobot,
  IconSparkles,
  IconUser,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AiProposalRecord } from "@/components/ai-operations/AiProposalRecord";
import {
  AiRunDecisionRecord,
  AiRunRevisionProgress,
} from "@/components/ai-operations/AiRunDecisionRecord";
import { ApprovalDecisionControls } from "@/components/approval/approval-decision-controls";
import { ApprovalStatusBadge } from "@/components/approval/approval-status-badge";
import { ErrorState } from "@/components/shared/error-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useApprovalProposalDetail } from "@/hooks/use-approvals";
import { usePermission } from "@/hooks/use-permission";
import { fetchAiOperationsRun } from "@/lib/ai-run-api";
import { formatRunFailureReason, runDisplayStatus } from "@/lib/ai-run-presentation";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { useFormatters } from "@/lib/format";
import { requireModule } from "@/lib/module-access";
import { formatBeijingDateTime } from "@/lib/time";

export const Route = createFileRoute("/_authed/ai-operations_/$runId")({
  beforeLoad: () => {
    requireModule("approvals");
  },
  component: AiRunRecordPage,
});

function AiRunRecordPage(): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { runId } = Route.useParams();
  const { hasPermission } = usePermission();
  const runQuery = useQuery({
    queryKey: ["ai-operations", "run", runId],
    queryFn: () => fetchAiOperationsRun(runId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.run.status === "running") return 3000;
      // 回滚补偿动作在执行中时持续刷新,否则"回滚中"要手动刷新才会变。
      const rollbackInFlight = data?.actions.some(
        (action) =>
          action.rollbackOfActionId !== null &&
          (action.status === "approved" || action.status === "executing"),
      );
      return rollbackInFlight ? 3000 : false;
    },
  });
  const detail = runQuery.data;
  const run = detail?.run;
  const currentProposal = detail?.currentProposal;
  const latestRevisionRound = detail?.proposalRounds.find(
    ({ closedReason }) => closedReason === "revision_requested",
  );
  const proposalDetailId =
    currentProposal?.id ?? (run?.status === "running" ? latestRevisionRound?.id : undefined);
  const proposalQuery = useApprovalProposalDetail(
    proposalDetailId,
    run?.status === "running",
    run?.shopId,
  );
  const proposal = proposalQuery.data?.data;
  const actions = detail?.actions ?? [];

  useEffect(() => {
    if (run?.status !== "running") return;
    const source = new EventSource(`/api/v1/ai-runs/${runId}/stream`);
    const refetch = () => {
      void runQuery.refetch();
      if (currentProposal?.id) void proposalQuery.refetch();
    };
    source.addEventListener("invalidate", refetch);
    source.addEventListener("phase", (event) => {
      try {
        const payload = JSON.parse(event.data) as { phase?: string };
        if (payload.phase === "completed" || payload.phase === "failed") source.close();
      } catch {
        source.close();
      }
    });
    source.addEventListener("done", () => source.close());
    return () => source.close();
  }, [currentProposal?.id, proposalQuery.refetch, run?.status, runId, runQuery.refetch]);

  if (run && currentProposal && proposalQuery.isError) {
    return (
      <main className="flex min-h-full min-w-[1024px] items-center justify-center bg-canvas px-6">
        <ErrorState
          message={t("runDetail.planLoadError")}
          onRetry={() => void proposalQuery.refetch()}
        />
      </main>
    );
  }

  if (run && currentProposal) {
    return (
      <main className="min-h-full min-w-[1024px] bg-canvas">
        <AiRunDecisionRecord
          key={`${currentProposal.id}:${proposal?.updatedAt ?? "pending"}`}
          actions={actions}
          canDecide={hasPermission("approve")}
          onApplied={() => {
            void Promise.all([runQuery.refetch(), proposalQuery.refetch()]);
          }}
          proposal={currentProposal}
          review={proposal}
          rounds={detail?.proposalRounds ?? []}
          run={run}
        />
      </main>
    );
  }

  if (run && latestRevisionRound && proposal) {
    return (
      <main className="min-h-full min-w-[1024px] bg-canvas">
        <AiRunRevisionProgress review={proposal} rounds={detail?.proposalRounds ?? []} run={run} />
      </main>
    );
  }

  return (
    <main className="flex min-h-full min-w-[1024px] flex-col gap-4 px-6 pb-8 pt-5">
      <header className="flex min-h-[66px] items-start justify-between gap-6 border-b border-line pb-4">
        <div className="min-w-0">
          <Link
            to="/ai-operations"
            search={DEFAULT_APPROVAL_SEARCH}
            className="mb-2 inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-ink"
          >
            <IconArrowLeft className="size-3" /> {t("runDetail.back")}
          </Link>
          <div className="flex items-center gap-2">
            <h1 className="truncate text-[24px] font-semibold leading-7 tracking-[-0.02em] text-ink">
              {run?.campaignName ?? t("runDetail.fallbackTitle")}
            </h1>
            {proposal ? (
              <ApprovalStatusBadge value={proposal.reviewStatus} />
            ) : run ? (
              <Badge variant="outline">{t(`runDetail.status.${run.status}`)}</Badge>
            ) : null}
          </div>
          <p className="mt-1 font-mono text-[10px] tabular-nums text-muted-foreground">
            {t("runDetail.runNumber", { id: runId.slice(0, 8).toUpperCase() })}
          </p>
        </div>
        {run ? <TriggerSummary run={run} /> : null}
      </header>

      {runQuery.isLoading ? <RunRecordSkeleton /> : null}
      {runQuery.isError ? (
        <ErrorState message={t("runDetail.loadError")} onRetry={() => void runQuery.refetch()} />
      ) : null}

      {run ? (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
          <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
            <div className="flex h-[48px] items-center justify-between border-b border-line px-4">
              <div>
                <h2 className="text-sm font-semibold text-ink">
                  {runRecordHeading(run, currentProposal != null, actions.length, t)}
                </h2>
                <p className="text-[10px] text-muted-foreground">
                  {runRecordDescription(run, currentProposal != null, actions.length, t)}
                </p>
              </div>
              <Badge variant="neutral" className="font-mono tabular-nums">
                {t("runDetail.actionsCount", {
                  count: currentProposal?.artifact.actions.length ?? run.candidateActions,
                })}
              </Badge>
            </div>
            {proposalQuery.isLoading ? (
              <div className="space-y-4 p-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-36 w-full" />
              </div>
            ) : proposalQuery.isError ? (
              <div className="p-4">
                <ErrorState
                  message={t("runDetail.planLoadError")}
                  onRetry={() => void proposalQuery.refetch()}
                />
              </div>
            ) : currentProposal ? (
              <div className="pt-5">
                <AiProposalRecord proposal={currentProposal} actions={actions} review={proposal} />
              </div>
            ) : run.jobType === "daily_search_term_mining" && actions.length > 0 ? (
              <MiningRunActions actions={actions} />
            ) : (
              <RunWithoutProposal run={run} />
            )}
          </section>

          <aside className="flex flex-col gap-4 xl:sticky xl:top-5">
            <RunContextCard run={run} />
            {detail.proposalRounds.length > 0 ? (
              <ProposalRoundHistory rounds={detail.proposalRounds} />
            ) : null}
            {proposal ? (
              <Card className="gap-3 py-4">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle>{t("runDetail.decision.title")}</CardTitle>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <ApprovalStatusBadge value={proposal.reviewStatus} />
                      {proposal.reviewStatus !== "rejected" ? (
                        <Badge variant="outline">
                          {t(`runDetail.finalization.${proposal.finalizationStatus}`)}
                        </Badge>
                      ) : null}
                      <ApprovalStatusBadge value={proposal.executionStatus} />
                    </div>
                  </div>
                  <p className="text-[11px] leading-4 text-muted-foreground">
                    {t("runDetail.decision.description")}
                  </p>
                </CardHeader>
                <CardContent>
                  {proposal.reviewStatus === "pending" &&
                  run.status === "suspended" &&
                  hasPermission("approve") ? (
                    <ApprovalDecisionControls
                      key={`${proposal.runId}:${proposal.updatedAt}`}
                      proposal={proposal}
                      onApplied={() => {
                        void Promise.all([runQuery.refetch(), proposalQuery.refetch()]);
                      }}
                    />
                  ) : proposal.reviewStatus === "pending" && run.status === "running" ? (
                    <p className="text-xs text-muted-foreground">
                      {t("runDetail.decision.processing")}
                    </p>
                  ) : !hasPermission("approve") ? (
                    <p className="text-xs text-muted-foreground">
                      {t("runDetail.decision.noPermission")}
                    </p>
                  ) : null}
                </CardContent>
              </Card>
            ) : null}
          </aside>
        </div>
      ) : null}
    </main>
  );
}

function ProposalRoundHistory({
  rounds,
}: {
  rounds: AiOperationsProposalRound[];
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="border-b">
        <CardTitle>{t("runDetail.rounds.title")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {rounds.map((round) => (
          <div
            key={round.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-line bg-subtle/40 p-2.5"
          >
            <div className="min-w-0">
              <p className="text-xs font-semibold text-ink">
                {t("runDetail.rounds.round", { number: round.roundNumber })}
              </p>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                {t("runDetail.rounds.counts", {
                  actions: round.actionCount,
                  conclusions: round.conclusionCount,
                })}
              </p>
            </div>
            <Badge variant={round.closedReason ? "neutral" : "outline"} className="shrink-0">
              {round.closedReason
                ? t("runDetail.rounds.revisionRequested")
                : t("runDetail.rounds.current")}
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function TriggerSummary({ run }: { run: AiOperationsRun }): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { locale } = useFormatters();
  const manual = run.triggerType === "manual";
  return (
    <div className="flex items-center gap-3 rounded-[10px] border border-line bg-surface px-3 py-2">
      <span className="flex size-8 items-center justify-center rounded-[8px] bg-subtle text-muted-foreground">
        {manual ? <IconUser className="size-4" /> : <IconRobot className="size-4" />}
      </span>
      <div>
        <p className="text-[10px] font-semibold text-ink">
          {manual ? t("runDetail.trigger.manual") : t("runDetail.trigger.scheduled")}
        </p>
        <p className="text-[9px] text-muted-foreground">
          {manual && run.triggeredByName
            ? t("runDetail.trigger.triggeredBy", { name: run.triggeredByName })
            : formatBeijingDateTime(run.startedAt, locale)}
        </p>
      </div>
    </div>
  );
}

function RunContextCard({ run }: { run: AiOperationsRun }): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { locale } = useFormatters();
  const rejected = runDisplayStatus(run, false).outcome === "rejected";
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="border-b">
        <CardTitle>{t("runDetail.context.title")}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        <RunFact label={t("runDetail.context.job")} value={t(`runDetail.job.${run.jobType}`)} />
        <RunFact
          label={t("runDetail.context.status")}
          value={rejected ? t("history.outcome.rejected") : t(`runDetail.status.${run.status}`)}
        />
        <RunFact
          label={t("runDetail.context.started")}
          value={formatBeijingDateTime(run.startedAt, locale)}
        />
        <RunFact
          label={t("runDetail.context.completed")}
          value={run.finishedAt ? formatBeijingDateTime(run.finishedAt, locale) : "—"}
        />
        <RunFact label={t("runDetail.context.autoExecuted")} value={String(run.autoExecuted)} />
        <RunFact label={t("runDetail.context.approvalRoute")} value={String(run.approvalCreated)} />
        <RunFact label={t("runDetail.context.skipped")} value={String(run.skipped)} />
        {rejected ? (
          <RunFact label={t("runDetail.context.rejected")} value={String(run.rejectedActions)} />
        ) : (
          <RunFact label={t("runDetail.context.failed")} value={String(run.failed)} />
        )}
      </CardContent>
    </Card>
  );
}

function RunWithoutProposal({ run }: { run: AiOperationsRun }): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  if (run.failureReason) {
    return (
      <div className="p-4">
        <Alert variant="error">
          <IconDatabaseOff />
          <AlertTitle>{t("runDetail.failed.title")}</AlertTitle>
          <AlertDescription>
            {formatRunFailureReason(run.failureReason, run.autoExecuted, t)}
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center px-8 text-center">
      <span className="flex size-11 items-center justify-center rounded-[12px] border border-line bg-subtle text-muted-foreground">
        {run.status === "running" ? (
          <IconSparkles className="size-5" />
        ) : (
          <IconCalendarTime className="size-5" />
        )}
      </span>
      <h3 className="mt-3 text-sm font-semibold text-ink">
        {run.status === "running"
          ? t("runDetail.noProposal.runningTitle")
          : t("runDetail.noProposal.noPlanTitle")}
      </h3>
      <p className="mt-1 max-w-[430px] text-[11px] leading-5 text-muted-foreground">
        {run.status === "running"
          ? t("runDetail.noProposal.runningBody")
          : t("runDetail.noProposal.noPlanBody")}
      </p>
    </div>
  );
}

function MiningRunActions({ actions }: { actions: AiOperationsRunAction[] }): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <div className="space-y-3 p-4">
      <Alert>
        <IconSparkles />
        <AlertTitle>{t("runDetail.mining.title")}</AlertTitle>
        <AlertDescription>{t("runDetail.mining.body")}</AlertDescription>
      </Alert>
      <div className="grid gap-2">
        {actions.map((action) => (
          <article
            key={action.id}
            className="flex items-center justify-between gap-4 rounded-[10px] border border-line bg-subtle/35 px-3.5 py-3"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate text-[11px] font-semibold text-ink">
                  {action.searchTermText ?? t("runDetail.mining.unavailableTerm")}
                </p>
                <Badge variant="outline" size="compact" className="shrink-0">
                  {action.actionType === "suggest_harvest_keyword"
                    ? t("runDetail.mining.harvestKeyword")
                    : t("runDetail.mining.negativeKeyword")}
                </Badge>
              </div>
              <p className="mt-1 text-[9px] text-muted-foreground">
                {t("runDetail.mining.actionMeta", {
                  confidence: t(`runDetail.record.confidenceValue.${action.confidence}`),
                  status: t(`runDetail.record.actionState.${action.status}`),
                })}
              </p>
            </div>
            <ApprovalStatusBadge value={action.riskLevel} className="shrink-0" />
          </article>
        ))}
      </div>
    </div>
  );
}

function runRecordHeading(
  run: AiOperationsRun,
  hasProposal: boolean,
  actionCount: number,
  t: (key: string) => string,
): string {
  if (hasProposal) return t("runDetail.heading.completePlan");
  if (run.jobType === "daily_search_term_mining" && actionCount > 0)
    return t("runDetail.heading.searchTermRecs");
  if (run.status === "running") return t("runDetail.heading.analysisInProgress");
  if (run.status === "failed") return t("runDetail.heading.runFailed");
  return t("runDetail.heading.runSummary");
}

function runRecordDescription(
  run: AiOperationsRun,
  hasProposal: boolean,
  actionCount: number,
  t: (key: string) => string,
): string {
  if (hasProposal) return t("runDetail.description.completePlan");
  if (run.jobType === "daily_search_term_mining" && actionCount > 0)
    return t("runDetail.description.searchTermRecs");
  if (run.status === "running") return t("runDetail.description.analysisInProgress");
  if (run.status === "failed") return t("runDetail.description.runFailed");
  return t("runDetail.description.runSummary");
}

function RunFact({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="min-w-0 rounded-[8px] border border-line bg-subtle/40 p-2.5">
      <p className="text-[9px] uppercase tracking-[0.04em] text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-[10px] font-semibold capitalize text-ink">{value}</p>
    </div>
  );
}

function RunRecordSkeleton(): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <div
      className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]"
      role="status"
      aria-label={t("runDetail.skeletonAria")}
    >
      <Skeleton className="h-[560px] w-full rounded-[14px]" />
      <Skeleton className="h-[320px] w-full rounded-[14px]" />
    </div>
  );
}
